interface ResourceNameRules {
  /** The resource the name belongs to, as it reads in a sentence: "report", "dashboard". */
  resource: string
  /** Longest name accepted, in Unicode code points. */
  maxLength?: number
  /** The saved name. A trimmed value equal to it is unchanged, not invalid. */
  current?: string
  /** A resource-specific rule, such as a duplicate-name check, run on the trimmed name. */
  validate?: (name: string) => string | undefined
}

type ResourceNameResult =
  | { status: "valid"; name: string }
  | { status: "unchanged"; name: string }
  | { status: "invalid"; name: string; message: string }

/**
 * Validates a name a user typed for a resource. The name is trimmed first, and every check runs on the trimmed value, so the returned `name` is what should be saved.
 *
 * Length is counted in code points rather than UTF-16 units, so an emoji counts as one character.
 *
 * @param value The raw input.
 * @param rules The resource's limits and the saved name to compare against.
 * @returns `valid` with the name to save, `unchanged` when there is nothing to save, or `invalid` with a message for the field.
 */
export function validateResourceName(
  value: string,
  rules: ResourceNameRules,
): ResourceNameResult {
  const name = value.trim()

  if (!name) {
    return {
      status: "invalid",
      name,
      message: `Enter a name for the ${rules.resource}.`,
    }
  }
  if (rules.current !== undefined && name === rules.current) {
    return { status: "unchanged", name }
  }
  if (
    rules.maxLength !== undefined &&
    Array.from(name).length > rules.maxLength
  ) {
    return {
      status: "invalid",
      name,
      message: `Use ${rules.maxLength} characters or fewer for the ${rules.resource} name.`,
    }
  }

  const message = rules.validate?.(name)
  if (message) {
    return { status: "invalid", name, message }
  }
  return { status: "valid", name }
}

/**
 * The message for a name `validateResourceName` rejects, in the shape `EditableTitle`'s `validate` prop takes.
 *
 * @returns The field message, or `undefined` when the name is valid or unchanged.
 */
export function resourceNameIssue(
  value: string,
  rules: ResourceNameRules,
): string | undefined {
  const result = validateResourceName(value, rules)
  return result.status === "invalid" ? result.message : undefined
}

/**
 * Whether two resource names count as the same name for a duplicate check: compared trimmed and without regard to case, so "Support bot" and "support bot " collide.
 */
export function isSameResourceName(a: string | null | undefined, b: string) {
  return (a ?? "").trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()
}
